import { Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "../auth/AuthProvider";
import { PageLoading } from "../components/PageLoading";
import { AssistantHost } from "../features/assistant/AssistantPanel";
import { zeitpunkt } from "../features/heute/briefing";
import { istEingabe } from "../features/heute/useAssistentTaste";
import { RUHT_HINWEIS } from "../lib/labels";
import { SectionTabs } from "./SectionTabs";
import {
  GOTO_KEYS,
  MAIN_SECTIONS,
  SECTIONS,
  breadcrumbFor,
  pageTitle,
  placementFor,
  type Section,
} from "./sections";
import { ShellStatusProvider, useShellStatus } from "./useShellData";


const FEED_WORD = { live: "Anrufimport aktuell", stale: "Anrufimport verzögert", unknown: "Anrufimport ohne Messung" } as const;

/**
 * Systemstreifen rechts in der Kopfzeile, aus einem einzigen
 * GET /betrieb/status. Stumm, solange nichts gemessen ist; nie "live" als
 * Vorgabe. Klick führt nach Betrieb.
 */
export function SystemStrip() {
  const { status } = useShellStatus();
  if (!status) return null;
  const now = new Date();
  const zulauf = status.zulauf;
  const stoerungen = status.stoerungen_gesamt ?? 0;
  const cerbosAus = status.komponenten.cerbos.erreichbar === false;
  const letzte = zulauf?.letzte_zusammenfassung_at
    ? `letzte Zusammenfassung ${zeitpunkt(zulauf.letzte_zusammenfassung_at, now).replace(/^um /, "")}`
    : null;
  const parts: string[] = [];
  if (zulauf) parts.push(FEED_WORD[zulauf.feed_state]);
  if (letzte) parts.push(letzte);
  if (cerbosAus) parts.push("Rechteprüfung nicht erreichbar");
  if (stoerungen > 0) parts.push(stoerungen === 1 ? "1 Störung" : `${stoerungen} Störungen`);
  if (parts.length === 0) return null;
  return (
    <Link to="/betrieb" className="lt-strip" aria-label={`Systemstatus: ${parts.join(", ")}. Zu Betrieb.`}>
      {zulauf ? (
        <span className="lt-strip-item lt-strip-feed">
          <i className={`lt-dot${zulauf.feed_state === "live" ? " is-live" : ""}`} aria-hidden="true" />
          {FEED_WORD[zulauf.feed_state]}
        </span>
      ) : null}
      {letzte ? <span className="lt-strip-item lt-strip-last">{letzte}</span> : null}
      {cerbosAus ? <span className="lt-strip-item is-danger">Rechteprüfung nicht erreichbar</span> : null}
      {stoerungen > 0 ? (
        <span className="lt-strip-item is-danger">{stoerungen === 1 ? "1 Störung" : `${stoerungen} Störungen`}</span>
      ) : null}
    </Link>
  );
}

function NavCount({ section }: { section: Section }) {
  const { status } = useShellStatus();
  const value = section.count?.(status) ?? null;
  if (value === null || value === 0) return null;
  return (
    <sup className="lt-count" aria-label={`${value} fällig`}>
      {value > 99 ? "99+" : value}
    </sup>
  );
}

function MainLinks({ active, onNavigate }: { active: Section | undefined; onNavigate?: () => void }) {
  return (
    <>
      {MAIN_SECTIONS.map((section) => (
        <Link
          key={section.to}
          to={section.to}
          className="lt-nav-link"
          aria-current={active === section ? "page" : undefined}
          onClick={onNavigate}
        >
          <span className="lt-nav-label">{section.label}</span>
          <NavCount section={section} />
        </Link>
      ))}
    </>
  );
}

const EXTRA_SECTIONS = SECTIONS.filter((s) => s.group === "experte");
const RUHT = SECTIONS.find((s) => s.group === "ruht")!;

/** "Mehr": Experte, die eingeklappte ruhende Sprachstrecke, Hilfe und Sitzung. */
function MoreMenu({
  onClose,
  onNavigate,
  anchor,
}: {
  onClose: () => void;
  onNavigate: () => void;
  anchor: DOMRect | null;
}) {
  const auth = useAuth();
  const ref = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const ruhtActive = placementFor(location.pathname)?.section === RUHT;

  useEffect(() => {
    ref.current?.querySelector<HTMLElement>("a")?.focus();
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        document.querySelector<HTMLElement>(".lt-more-button")?.focus();
      }
    };
    const onDown = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (ref.current && !ref.current.contains(target) && !target.closest(".lt-more-button")) onClose();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onDown);
    };
  }, [onClose]);

  const style = anchor && window.innerWidth >= 720 ? { left: Math.max(16, anchor.left - 24) } : undefined;

  return (
    <div className="lt-more" ref={ref} role="dialog" aria-label="Mehr" style={style}>
      {EXTRA_SECTIONS.map((section) => (
        <div key={section.to}>
          <p className="lt-more-label">{section.label}</p>
          <ul className="lt-more-list">
            {section.tabs.map((tab) => (
              <li key={tab.to}><Link to={tab.to} onClick={onNavigate}>{tab.label}</Link></li>
            ))}
          </ul>
        </div>
      ))}
      <details className="lt-more-rest" open={ruhtActive || undefined}>
        <summary>Eigene Sprachstrecke (ruht)</summary>
        <p className="lt-more-note">{RUHT_HINWEIS}.</p>
        <ul className="lt-more-list lt-more-cols">
          {RUHT.tabs.map((tab) => (
            <li key={tab.to}>
              <Link to={tab.to} onClick={onNavigate}>
                {tab.label}
              </Link>
            </li>
          ))}
        </ul>
      </details>
      <div className="lt-more-foot">
        <Link to="/hilfe" onClick={onNavigate}>
          Hilfe
        </Link>
        {auth.mode === "dev" ? (
          <span className="lt-more-note">Entwicklungsmodus ohne Anmeldung</span>
        ) : (
          <button type="button" className="lt-link-button" onClick={() => auth.logout()}>
            Abmelden
          </button>
        )}
      </div>
    </div>
  );
}

function Shell({ children }: { children: ReactNode }) {
  const location = useLocation();
  const navigate = useNavigate();
  const placement = placementFor(location.pathname);
  const crumbs = breadcrumbFor(location.pathname);
  const [more, setMore] = useState<DOMRect | null | false>(false);
  const pendingGoto = useRef<number | null>(null);
  const focusMain = useRef(false);
  const active = placement?.section.group === "main" ? placement.section : undefined;
  const ruht = placement?.section.group === "ruht";

  useEffect(() => {
    document.title = `${pageTitle(location.pathname)} · strawatar Leitstand`;
    setMore(false);
    // Neuer Bereich beginnt oben; sonst landet man mitten in einer kürzeren Seite.
    window.scrollTo(0, 0);
    // Nach einer Wahl im Menü "Mehr" fiele der Fokus sonst auf body: zum Inhalt.
    if (focusMain.current) {
      focusMain.current = false;
      document.getElementById("inhalt")?.focus();
    }
  }, [location.pathname]);

  // Keyboard: "?" opens help, "g" then a letter jumps to a section.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (istEingabe(event.target)) return;
      if (event.key === "?") {
        event.preventDefault();
        navigate("/hilfe");
        return;
      }
      if (event.key === "g") {
        pendingGoto.current = Date.now();
        return;
      }
      const armed = pendingGoto.current;
      pendingGoto.current = null;
      if (armed !== null && Date.now() - armed < 1500) {
        const target = GOTO_KEYS[event.key];
        if (target) {
          event.preventDefault();
          navigate(target);
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [navigate]);

  const moreActive = placement?.section.group === "experte" || ruht;

  return (
    // Auf dem Rückruf am Telefon trägt die Aktionsleiste aus Paket 3 den unteren Rand.
    <div className={`lt-shell${location.pathname.startsWith("/rueckruf/") ? " is-fokus" : ""}`}>
      <a className="skip-link" href="#inhalt">
        Zum Inhalt springen
      </a>
      <header className="lt-header">
        <Link to="/" className="lt-brand" aria-label="strawatar Leitstand, zu Heute">
          strawatar<span>Leitstand</span>
        </Link>
        <nav className="lt-nav" aria-label="Hauptnavigation">
          <MainLinks active={active} />
          <button
            type="button"
            className="lt-nav-link lt-more-button"
            aria-haspopup="dialog"
            aria-expanded={more !== false}
            aria-current={moreActive ? "true" : undefined}
            onClick={(event) => setMore(more === false ? event.currentTarget.getBoundingClientRect() : false)}
          >
            Mehr
          </button>
        </nav>
        <div className="lt-header-end">
          <SystemStrip />
        </div>
      </header>
      {more !== false ? <MoreMenu
          anchor={more}
          onClose={() => setMore(false)}
          onNavigate={() => {
            focusMain.current = true;
            setMore(false);
            // Gleiche Route gewählt: kein Seitenwechsel, also hier fokussieren.
            window.setTimeout(() => {
              if (!focusMain.current) return;
              focusMain.current = false;
              document.getElementById("inhalt")?.focus();
            }, 0);
          }}
        /> : null}
      {placement && placement.section.tabs.length > 0 && !placement.detail ? (
        <SectionTabs section={placement.section} pathname={location.pathname} />
      ) : null}
      <main id="inhalt" className="lt-main" tabIndex={-1}>
        {crumbs.length > 0 ? (
          <nav className="lt-crumbs" aria-label="Pfadnavigation">
            {crumbs.map((crumb, index) => (
              <span key={crumb.label}>
                {index > 0 ? <span aria-hidden="true"> / </span> : null}
                {crumb.to ? <Link to={crumb.to}>{crumb.label}</Link> : <span aria-current="page">{crumb.label}</span>}
              </span>
            ))}
          </nav>
        ) : null}
        {ruht ? (
          <p className="lt-ruht" role="note">
            {RUHT_HINWEIS}.
          </p>
        ) : null}
        {/* Die Suspense-Grenze gehoert hierher, nicht um das ganze Layout:
            sonst verschwindet bei jedem Seitenwechsel die Huelle. */}
        <AssistantHost>
          <Suspense fallback={<PageLoading />}>{children}</Suspense>
        </AssistantHost>
      </main>
      <nav className="lt-tabbar" aria-label="Hauptnavigation unten">
        <MainLinks active={active} />
      </nav>
    </div>
  );
}

export function AppLayout({ children }: { children: ReactNode }) {
  return (
    <ShellStatusProvider>
      <Shell>{children}</Shell>
    </ShellStatusProvider>
  );
}
